import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useIdleTimer } from "react-idle-timer";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { dialog_border_radius } from "styles/Dimensions";
import { Button } from "@verily-src/react-design-system";
import pluralize from "pluralize";
import A11yDialogTitle from "components/A11yDialogTitle";
import { telemetry } from "./TelemetryController";

// Set 15-minute idle timeout.
const timeout = 1000 * 60 * 15;

// Show the warning dialog 2 minutes before idle timeout.
const promptBeforeIdle = 1000 * 60 * 2;

// Refresh Auth0 token every 10 minutes to keep the Auth0 session alive.
const auth0TokenRefeshTime = 1000 * 60 * 10;

// Monitor user activity and show a warning dialog before idle timout.
const SessionController: React.FC = () => {
  const { isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
  const [remainingTimeSeconds, setRemainingTimeSeconds] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);

  const closeSession = useCallback(() => {
    logout({
      returnTo: window.location.origin,
    });
  }, [logout]);

  const onIdle = () => {
    closeSession();
  };

  const onPrompt = () => {
    if (isAuthenticated) {
      setOpen(true);
    } else {
      closeSession();
    }
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });

  const stayLoggedIn = useCallback(async () => {
    // Reactivate the idle timer
    activate();
    setOpen(false);
  }, [activate]);

  // Refresh the Auth0 token every 10 minutes to keep Auth0 session alive.
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isAuthenticated) {
        try {
          // Make a request to keep Auth0 session alive.
          await getAccessTokenSilently();
        } catch (error) {
          telemetry.logError(error);

          // Do not close session in case that getAccessTokenSilently() failed temporarily.
        }
      } else {
        telemetry.logError(new Error("session expired"));
        closeSession();
      }
    }, auth0TokenRefeshTime);

    return () => {
      clearInterval(interval);
    };
  }, [closeSession, getAccessTokenSilently, isAuthenticated]);

  // Update the remaining time in seconds.
  useEffect(() => {
    if (open) {
      const interval = setInterval(() => {
        setRemainingTimeSeconds(Math.ceil(getRemainingTime() / 1000));
      }, 500);

      return () => {
        clearInterval(interval);
      };
    }
  }, [getRemainingTime, open]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        style: { borderRadius: dialog_border_radius },
        sx: { width: "500px !important" },
      }}
    >
      <A11yDialogTitle sx={{ paddingTop: "24px" }}>
        <Typography variant="display6">Are you still there?</Typography>
      </A11yDialogTitle>
      <DialogContent sx={{ paddingBottom: "0px" }}>
        <Typography variant="body1" sx={{ marginBottom: "16px" }}>
          We log you out when you've been inactive for more than 15 minutes.
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: "16px" }}>
          You have{" "}
          <b>
            {pluralize("minute", Math.floor(remainingTimeSeconds / 60), true)}{" "}
            and {pluralize("second", remainingTimeSeconds % 60, true)}
          </b>{" "}
          before logout.
        </Typography>
        <Typography variant="body1" sx={{ paddingBottom: "4px" }}>
          If you're still working, stay logged in.
        </Typography>
      </DialogContent>
      <DialogActions
        style={{ justifyContent: "right" }}
        sx={{ padding: "16px 24px 24px 24px" }}
      >
        <Button
          label="Log out"
          onClick={() => {
            closeSession();
          }}
          variant="outlined"
          sx={{
            marginRight: "4px",
          }}
        />
        <Button
          label="Stay logged in"
          variant="filled"
          onClick={() => {
            stayLoggedIn();
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SessionController;
